// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { userApi } from '@advisor-app/store/api/engine/user-api';
import { useCallback, useEffect, useState } from 'react';
import { useSafeUpdate } from '@shared/ui-kit/hooks';
import { type UserBase } from '@shared/data-access/interfaces/api/engine/citec';
import { useAppDispatch, useAppSelector } from '@advisor-app/store/store';
import { selectUserBase, userSlice } from '@shared/data-access/slices/user';

export const useAccount = () => {
  const runSafeUpdate = useSafeUpdate();
  const dispatch = useAppDispatch();

  const userBaseInfo = useAppSelector(selectUserBase);

  const [updateUser] = userApi.useUpdateUserMutation();

  const [accountData, setAccountData] = useState<UserBase>({
    first_name: userBaseInfo.first_name,
    second_name: userBaseInfo.second_name,
    first_surname: userBaseInfo.first_surname,
    second_surname: userBaseInfo.second_surname,
    phone_number: userBaseInfo.phone_number,
  });

  const handleEditAccountField = useCallback(
    (field: keyof UserBase, text: string) => {
      setAccountData(prevState => ({ ...prevState, [field]: text }));
    },
    [],
  );

  const handleUpdateUserInfo = useCallback(async () => {
    const hasData = Object.values(accountData).some(value => value !== null);

    if (hasData && JSON.stringify(userBaseInfo) !== JSON.stringify(accountData)) {
      try {
        const data = await updateUser({
          payload: {
            first_name: accountData.first_name,
            second_name: accountData.second_name,
            first_surname: accountData.first_surname,
            second_surname: accountData.second_surname,
            phone_number: accountData.phone_number,
          },
        }).unwrap();
        /* istanbul ignore next */
        runSafeUpdate(() =>
          dispatch(
            userSlice.actions.setUserBase({
              first_name: data.first_name,
              second_name: data.second_name,
              first_surname: data.first_surname,
              second_surname: data.second_surname,
              phone_number: data.phone_number,
            }),
          ),
        );
      } catch (error) {
        console.error('[handleUpdateUserInfo]', error);
      }
    }
  }, [accountData, dispatch, runSafeUpdate, updateUser, userBaseInfo]);

  useEffect(() => {
    setAccountData(userBaseInfo);
  }, [userBaseInfo]);

  return {
    accountData,
    setAccountData,
    handleUpdateUserInfo,
    handleEditAccountField,
  };
};
